// Voice for the Copilot (2026-10-06): speech-to-text with the browser's own
// recogniser (Chrome / Edge, desktop and Android; Hindi or Indian English) and
// text-to-speech with speechSynthesis. Audio never leaves the browser for the
// AI provider — only the recognised text is sent, like a typed question.
import { useCallback, useEffect, useRef, useState } from 'react';

interface RecognitionResult { isFinal: boolean; 0: { transcript: string } }
interface RecognitionEvent { resultIndex: number; results: ArrayLike<RecognitionResult> }
interface Recognition {
  lang: string; continuous: boolean; interimResults: boolean; maxAlternatives: number;
  start(): void; stop(): void; abort(): void;
  onresult: ((e: RecognitionEvent) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}
type RecognitionCtor = new () => Recognition;

const ctor = (): RecognitionCtor | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

export type VoiceLang = 'en-IN' | 'hi-IN';

/** Listen once; `onText(text, final)` gets interim and final transcripts. */
export function useSpeechInput(onText: (text: string, final: boolean) => void) {
  const supported = !!ctor();
  const [listening, setListening] = useState(false);
  const [error, setError] = useState('');
  const rec = useRef<Recognition | null>(null);
  const cb = useRef(onText);
  cb.current = onText;

  const stop = useCallback(() => { rec.current?.stop(); }, []);
  const start = useCallback((lang: VoiceLang) => {
    const C = ctor();
    if (!C) { setError('Voice input works in Chrome or Edge.'); return; }
    rec.current?.abort();
    const r = new C();
    r.lang = lang; r.continuous = false; r.interimResults = true; r.maxAlternatives = 1;
    let finalText = '';
    r.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const res = e.results[i];
        if (res.isFinal) finalText += res[0].transcript; else interim += res[0].transcript;
      }
      cb.current((finalText + interim).trim(), false);
    };
    r.onerror = (e) => {
      setError(e.error === 'not-allowed' || e.error === 'service-not-allowed' ? 'Allow microphone access to speak.' : e.error === 'no-speech' ? 'Didn\'t hear anything — try again.' : 'Voice input stopped.');
    };
    r.onend = () => { setListening(false); if (finalText.trim()) cb.current(finalText.trim(), true); };
    rec.current = r;
    setError('');
    setListening(true);
    try { r.start(); } catch { setListening(false); }
  }, []);
  useEffect(() => () => rec.current?.abort(), []);
  return { supported, listening, error, start, stop };
}

/** Read text aloud in a matching Indian voice (Hindi for Devanagari text). */
export function useSpeechOutput() {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [speaking, setSpeaking] = useState(false);
  const speak = useCallback((text: string) => {
    if (!supported || !text.trim()) return;
    const synth = window.speechSynthesis;
    synth.cancel();
    const hindi = /[ऀ-ॿ]/.test(text);
    const u = new SpeechSynthesisUtterance(text.replace(/₹/g, 'rupees ').slice(0, 1500));
    u.lang = hindi ? 'hi-IN' : 'en-IN';
    const voice = synth.getVoices().find((v) => v.lang === u.lang) || synth.getVoices().find((v) => v.lang.startsWith(hindi ? 'hi' : 'en-IN'));
    if (voice) u.voice = voice;
    u.rate = 1;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(u);
  }, [supported]);
  const cancel = useCallback(() => { if (supported) window.speechSynthesis.cancel(); setSpeaking(false); }, [supported]);
  useEffect(() => () => { if (supported) window.speechSynthesis.cancel(); }, [supported]);
  return { supported, speaking, speak, cancel };
}
